import React from "react";
import { Badge, Button, Divider, Group, Select, Stack, Switch, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  ConfigKeyOption,
  LogModeOption,
  PlayerPrefChannelKey,
  PlayerPrefKey,
  RESET_KEY_CONFIG_COMMAND,
  configKeyToValue,
  getLogModeOptions,
  normalizeLogMode,
} from "@utils/deviceConfigKeys";
import ApiConfigOverrideControl from "./ApiConfigOverrideControl";

export type DevicePlayerPrefChange =
  | { kind: "configKey"; value: ConfigKeyOption }
  | { kind: "enableLogger"; value: boolean }
  | { kind: "enableFileLogger"; value: boolean }
  | { kind: "enableLokiLogger"; value: boolean }
  | { kind: "logMode"; value: LogModeOption }
  | { kind: "disableKeyConfigOnTest"; value: boolean }
  | { kind: "resetKeyConfig" };

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const playerPrefValue = (change: DevicePlayerPrefChange): string => {
  switch (change.kind) {
    case "configKey":
      return configKeyToValue(change.value);
    case "logMode":
      return change.value;
    case "resetKeyConfig":
      return "true";
    default:
      return change.value ? "true" : "false";
  }
};

export const confirmationCopy = (
  pending: DevicePlayerPrefChange,
  t: Translate,
): { title: string; message: string; color: string } => {
  const booleanCopy = (name: string, on: boolean) =>
    on
      ? {
          title: t(`DeviceConfig.confirm.enable${name}Title`),
          message: t(`DeviceConfig.confirm.enable${name}Message`),
          color: "blue",
        }
      : {
          title: t(`DeviceConfig.confirm.disable${name}Title`),
          message: t(`DeviceConfig.confirm.disable${name}Message`),
          color: "red",
        };

  switch (pending.kind) {
    case "configKey":
      return {
        title: t("DeviceConfig.confirm.configKeyTitle"),
        message:
          pending.value === "unset"
            ? t("DeviceConfig.confirm.configKeyUnset")
            : t("DeviceConfig.confirm.configKeySet", { value: configKeyToValue(pending.value) }),
        color: "blue",
      };
    case "enableLogger":
      return booleanCopy("Logger", pending.value);
    case "enableFileLogger":
      return booleanCopy("FileLogger", pending.value);
    case "enableLokiLogger":
      return booleanCopy("LokiLogger", pending.value);
    case "logMode":
      return {
        title: t("DeviceConfig.confirm.logModeTitle", {
          value: t(`DeviceConfig.logMode.${pending.value}`),
        }),
        message: t(`DeviceConfig.confirm.logMode.${pending.value}`),
        color: pending.value === "verbose" || pending.value === "off" ? "red" : "blue",
      };
    case "disableKeyConfigOnTest":
      return pending.value
        ? {
            title: t("DeviceConfig.confirm.disableKeysTitle"),
            message: t("DeviceConfig.confirm.disableKeysMessage"),
            color: "blue",
          }
        : {
            title: t("DeviceConfig.confirm.enableKeysTitle"),
            message: t("DeviceConfig.confirm.enableKeysMessage"),
            color: "red",
          };
    case "resetKeyConfig":
      return {
        title: t("DeviceConfig.confirm.resetKeysTitle"),
        message: t("DeviceConfig.confirm.resetKeysMessage"),
        color: "red",
      };
    default:
      throw new Error(`Unhandled player pref change: ${JSON.stringify(pending)}`);
  }
};

interface DevicePlayerPrefsControlsProps {
  configKey: ConfigKeyOption;
  enableLogger: boolean;
  enableFileLogger: boolean;
  enableLokiLogger: boolean;
  logMode: LogModeOption;
  disableKeyConfigOnTest: boolean;
  pendingChange?: DevicePlayerPrefChange | null;
  queued?: Partial<Record<PlayerPrefKey, string>>;
  working?: PlayerPrefChannelKey | null;
  showDividers?: boolean;
  expectedSerial?: string | null;
  onConfigKeyChange: (value: ConfigKeyOption) => void;
  onRequestChange: (change: DevicePlayerPrefChange) => void;
  supports: (key: PlayerPrefChannelKey) => boolean;
  tooOldNote: (label: string, key: PlayerPrefChannelKey) => React.ReactNode;
}

export const QueuedBadge: React.FC<{ value?: string }> = ({ value }) => {
  const { t } = useTranslation();
  if (value === undefined) return null;
  return (
    <Badge color="yellow" variant="light" size="sm" data-testid="player-pref-queued">
      {t("DeviceConfig.pendingBadge", { value: value || t("DeviceConfig.pendingUnset") })}
    </Badge>
  );
};

const PrefSwitch: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  queuedValue?: string;
  onChange: (checked: boolean) => void;
  children?: React.ReactNode;
}> = ({ title, description, checked, disabled, queuedValue, onChange, children }) => {
  const { t } = useTranslation();
  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={5}>{title}</Title>
            <QueuedBadge value={queuedValue} />
          </Group>
          <Text size="xs" c="dimmed">
            {description}
          </Text>
        </Stack>
        <Switch
          size="lg"
          onLabel={t("DeviceConfig.on")}
          offLabel={t("DeviceConfig.off")}
          aria-label={title}
          checked={checked}
          onChange={(e) => onChange(e.currentTarget.checked)}
          disabled={disabled}
        />
      </Group>
      {children}
    </Stack>
  );
};

const DevicePlayerPrefsControls: React.FC<DevicePlayerPrefsControlsProps> = ({
  configKey,
  enableLogger,
  enableFileLogger,
  enableLokiLogger,
  logMode,
  disableKeyConfigOnTest,
  pendingChange = null,
  queued = {},
  working = null,
  showDividers = true,
  expectedSerial,
  onConfigKeyChange,
  onRequestChange,
  supports,
  tooOldNote,
}) => {
  const { t } = useTranslation();
  const pendingLogger = pendingChange?.kind === "enableLogger" ? pendingChange.value : null;
  const pendingFileLogger = pendingChange?.kind === "enableFileLogger" ? pendingChange.value : null;
  const pendingLokiLogger = pendingChange?.kind === "enableLokiLogger" ? pendingChange.value : null;
  const pendingLogMode = pendingChange?.kind === "logMode" ? pendingChange.value : null;
  const pendingKeyConfig =
    pendingChange?.kind === "disableKeyConfigOnTest" ? pendingChange.value : null;
  const keyConfigSupported = supports("disableKeyConfigOnTest");

  const loggerSwitches: {
    key: "enableLogger" | "enableFileLogger" | "enableLokiLogger";
    checked: boolean;
  }[] = [
    { key: "enableLogger", checked: pendingLogger ?? enableLogger },
    { key: "enableFileLogger", checked: pendingFileLogger ?? enableFileLogger },
    { key: "enableLokiLogger", checked: pendingLokiLogger ?? enableLokiLogger },
  ];

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Group gap="xs">
          <Title order={5}>{t("DeviceConfig.apiOverrideTitle")}</Title>
          <QueuedBadge value={queued.configKey} />
        </Group>
        <Text size="xs" c="dimmed">
          {t("DeviceConfig.apiOverrideDescription")}
        </Text>
        <ApiConfigOverrideControl
          configKey={configKey}
          onlineSupported={supports("configKey")}
          onlineWorking={working === "configKey"}
          onlineUnsupportedNote={
            tooOldNote(t("DeviceConfig.apiOverrideTitle"), "configKey") ?? (
              <Text size="xs" c="dimmed">
                {t("DeviceConfig.selectDeviceForOnline")}
              </Text>
            )
          }
          expectedSerial={expectedSerial}
          onConfigKeyChange={onConfigKeyChange}
          onApplyOnline={() => onRequestChange({ kind: "configKey", value: configKey })}
        />
      </Stack>

      {loggerSwitches.map(({ key, checked }) => (
        <React.Fragment key={key}>
          {showDividers && <Divider />}
          {!supports(key) ? (
            tooOldNote(t(`DeviceConfig.${key}Title`), key)
          ) : (
            <PrefSwitch
              title={t(`DeviceConfig.${key}Title`)}
              description={t(`DeviceConfig.${key}Description`)}
              checked={checked}
              disabled={working === key}
              queuedValue={queued[key]}
              onChange={(value) => onRequestChange({ kind: key, value })}
            />
          )}
        </React.Fragment>
      ))}

      {showDividers && <Divider />}

      {!supports("logMode") ? (
        tooOldNote(t("DeviceConfig.logModeTitle"), "logMode")
      ) : (
        <Stack gap="xs">
          <Stack gap={4}>
            <Group gap="xs">
              <Title order={5}>{t("DeviceConfig.logModeTitle")}</Title>
              <QueuedBadge value={queued.logMode} />
            </Group>
            <Text size="xs" c="dimmed">
              {t("DeviceConfig.logModeDescription")}
            </Text>
          </Stack>
          <Select
            aria-label={t("DeviceConfig.logModeTitle")}
            data={getLogModeOptions(t)}
            value={pendingLogMode ?? logMode}
            onChange={(v) => v && onRequestChange({ kind: "logMode", value: normalizeLogMode(v) })}
            allowDeselect={false}
            disabled={working === "logMode"}
            maw={420}
          />
        </Stack>
      )}

      {showDividers && <Divider />}

      {!keyConfigSupported ? (
        tooOldNote(t("DeviceConfig.disableKeysTitle"), "disableKeyConfigOnTest")
      ) : (
        <PrefSwitch
          title={t("DeviceConfig.disableKeysTitle")}
          description={t("DeviceConfig.disableKeysDescription")}
          checked={pendingKeyConfig ?? disableKeyConfigOnTest}
          disabled={working === "disableKeyConfigOnTest" || working === RESET_KEY_CONFIG_COMMAND}
          queuedValue={queued.disableKeyConfigOnTest}
          onChange={(value) => onRequestChange({ kind: "disableKeyConfigOnTest", value })}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="xs" c="dimmed">
              {t("DeviceConfig.resetKeysDescription")}
            </Text>
            <Button
              variant="default"
              size="xs"
              onClick={() => onRequestChange({ kind: "resetKeyConfig" })}
              loading={working === RESET_KEY_CONFIG_COMMAND}
              disabled={working === "disableKeyConfigOnTest"}
              style={{ flexShrink: 0 }}
            >
              {t("DeviceConfig.resetKeys")}
            </Button>
          </Group>
        </PrefSwitch>
      )}
    </Stack>
  );
};

export default DevicePlayerPrefsControls;
